/**
 * @fileoverview firebase 認証
 */

import {
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  TwitterAuthProvider,
  updatePassword,
  User,
  verifyBeforeUpdateEmail,
} from "firebase/auth";
import { firebaseAuth } from "../firebase/config";

//------------
// プロバイダID
//------------
const PROVIDER_EMAIL = "password";
const PROVIDER_GOOGLE = "google.com";
const PROVIDER_TWITTER = "twitter.com";

//------------
// 認証エラーコード
//------------
export const ERROR_AUTH_RECENT_LOGIN = "auth/requires-recent-login";
export const ERROR_AUTH_EMAIL_ALREADY_IN_USE = "auth/email-already-in-use";
export const ERROR_AUTH_INVALID_EMAIL = "auth/invalid-email";
export const ERROR_AUTH_INVALID_NEW_EMAIL = "auth/invalid-new-email";
export const ERROR_AUTH_MISSING_NEW_EMAIL = "auth/missing-new-email";

/**
 * サインアップ：メールアドレス
 */
export async function fbSignUpWithMail(email: string, password: string): Promise<string> {
  //------------
  // ユーザーアカウントを登録
  //------------
  const userCredential = await createUserWithEmailAndPassword(firebaseAuth, email, password);

  console.log("ユーザーアカウントを登録");
  console.log(userCredential.user);

  //------------
  // 登録メールアドレスに確認メールを送信
  //------------
  await sendEmailVerification(userCredential.user);

  return userCredential.user.uid;
}

/**
 * サインイン：メールアドレス
 */
export async function fbSignInWithMail(email: string, password: string) {
  // サインイン
  await signInWithEmailAndPassword(firebaseAuth, email, password);
}

/**
 * サインアップ／サインイン：Googleアカウント
 *   @note サインアップとサインインの処理は同じ。
 *         登録済みか否かによって、サインアップかサインインかが決まる。
 */
export async function fbSignUpAndInWithGoogle(): Promise<string> {
  // ユーザーアカウントを登録
  const provider = new GoogleAuthProvider();

  // @note signInWithRedirect()ではうまくいかなかった
  const result = await signInWithPopup(firebaseAuth, provider);

  return result.user.uid;
}

/**
 * サインアップ／サインイン：X(Twitter)アカウント
 *   @note サインアップとサインインの処理は同じ。
 *         登録済みか否かによって、サインアップかサインインかが決まる。
 */
export async function fbSignUpAndInWithTwitter(): Promise<string> {
  // ユーザーアカウントを登録
  const provider = new TwitterAuthProvider();

  // @note signInWithRedirect()ではうまくいかなかった
  const result = await signInWithPopup(firebaseAuth, provider);

  return result.user.uid;
}

/**
 * サインアウト
 */
export async function fbSignOut() {
  // サインアウト
  await signOut(firebaseAuth);
}

/**
 * アカウント削除
 */
export async function fbDeleteAccount() {
  const user = firebaseAuth.currentUser!;

  // アカウント削除
  await deleteUser(user);
}

/**
 * アカウント再認証
 */
export async function fbReAuth(user: User) {
  //------------
  // 認証プロバイダに応じた再認証
  //------------
  // ループさせているのは、複数プロバイダがリンクできる仕様となっているため。
  // ただ、本アプリではリンクしていないため、1つだけとなる（実質1回のループしか発生しない）
  for (let i = 0; i < user.providerData.length; i++) {
    console.log(
      "firebaseアカウント再認証 user.providerData[i].providerId=" + user.providerData[i].providerId
    );

    // google
    if (user.providerData[i].providerId === PROVIDER_GOOGLE) {
      fbReAuthWithGoogle(user);
      return;
    }

    // X(Twitter)
    if (user.providerData[i].providerId === PROVIDER_TWITTER) {
      fbReAuthWithTwitter(user);
      return;
    }

    // メールアドレス／パスワード
    if (user.providerData[i].providerId === PROVIDER_EMAIL) {
      console.log("firebaseアカウント再認証 password ルートチェック");
      fbReAuthWithMail(user);
      return;
    }
  }
}

/**
 * アカウント再認証：プロバイダ＝Google
 */
export async function fbReAuthWithGoogle(user: User) {
  // [TODO] エラー対応
  // Googleの再認証用ポップアップを表示
  const provider = new GoogleAuthProvider();
  const result = await reauthenticateWithPopup(user, provider);

  // Googleプロバイダの credential を作成
  const credential = GoogleAuthProvider.credentialFromResult(result);

  // 再認証を実行
  await reauthenticateWithCredential(user, credential!);
}

/**
 * アカウント再認証：プロバイダ＝X(Twitter)
 */
export async function fbReAuthWithTwitter(user: User) {
  // [TODO] エラー対応
  // X(Twitter)の再認証用ポップアップを表示
  const provider = new TwitterAuthProvider();
  const result = await reauthenticateWithPopup(user, provider);

  // X(Twitter)プロバイダの credential を作成
  const credential = TwitterAuthProvider.credentialFromResult(result);

  // 再認証を実行
  await reauthenticateWithCredential(user, credential!);
}

/**
 * アカウント再認証：プロバイダ＝メールアドレス
 */
export async function fbReAuthWithMail(user: User) {
  // [TODO] エラー対応
  // ユーザーに現在のパスワードを入力させる
  const currentPassword = prompt("再認証が必要です。パスワードを入力してください");
  if (!currentPassword) {
    throw new Error("パスワードnull");
  }

  // メールアドレスとパスワードでcredentialを作成
  const credential = EmailAuthProvider.credential(user.email!, currentPassword);

  // 再認証を実行
  await reauthenticateWithCredential(user, credential);
}

/**
 * メールアドレス変更
 *   @note updateEmail()は使用不可。「auth/operation-not-allowed」が発生するため。
 */
export async function fbChangeEmail(email: string) {
  const user = firebaseAuth.currentUser!;

  // メールアドレス更新
  await verifyBeforeUpdateEmail(user, email);

  console.log("verifyBeforeUpdateEmail()をコール");
}

/**
 * パスワード再設定リンクメール送信
 */
export async function fbSendResetPasswordMail(email: string) {
  await sendPasswordResetEmail(firebaseAuth, email);
}

/**
 * パスワード再設定（ログイン中）
 *   現在のパスワードを検証するために、再認証を行う
 */
export async function fbChangePassword(currentPassword: string, newPassword: string) {
  const user = firebaseAuth.currentUser!;

  //------------
  // 現在のパスワードの検証
  //------------
  // 現在のパスワードでcredentialを作成
  const credential = EmailAuthProvider.credential(user.email!, currentPassword);
  // 現在のパスワードで再認証することで、現在のパスワードが合っているか確認
  await reauthenticateWithCredential(user, credential);

  //------------
  // パスワードの更新
  //------------
  await updatePassword(user, newPassword);
}

/**
 * パスワード再設定
 *   ユーザーが「パスワードを忘れた」フローで、
 *   メールから届いたリセットリンクをクリックして、新しいパスワードを設定するときに使用
 */
export async function fbResetPassword(oobCode: string, newPassword: string) {
  // パスワードリセットAPI
  // @note サインイン中に実行すると、そのユーザーは強制でサインアウトされる
  await confirmPasswordReset(firebaseAuth, oobCode, newPassword);
}

/**
 * メールアドレス変更の確定
 */
export async function fbApplyEmailChange(oobCode: string) {
  console.log("メール認証状況=" + firebaseAuth.currentUser?.emailVerified);

  // メール認証完了
  // @note 新規登録／メールアドレス変更のどちらでも必要
  await applyActionCode(firebaseAuth, oobCode);
}

/**
 * メール認証URLの送信
 */
export async function fbSendAuthenticateEmailUrl() {
  const user = firebaseAuth.currentUser!;

  // 登録メールアドレスに確認メールを送信
  await sendEmailVerification(user);
}
